import { DomainsIcon } from "@minket/brand/icons/domains";
import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useState } from "react";
import type { ConsoleDomain } from "@/components/domains/add-domain-form.ondemand";
import { StepPage } from "@/components/onboarding/step-page";
import { UnavailableAction } from "@/components/page/action";
import { ProjectScope } from "@/components/page/scope";
import { useCan } from "@/lib/permissions";

export const Route = createFileRoute("/onboarding/domain")({
  component: DomainStep,
});

const AddDomainForm = lazy(() =>
  import("@/components/domains/add-domain-form.ondemand").then((module) => ({
    default: module.AddDomainForm,
  })),
);

/** The domain just added, with its micro seal and records, once there is one. */
const AddedDomain = lazy(() =>
  import("@/components/domains/added-domain.ondemand").then((module) => ({
    default: module.AddedDomain,
  })),
);

function DomainStep() {
  const [added, setAdded] = useState<ConsoleDomain | null>(null);
  const canAdd = useCan("domains.manage");
  return (
    <StepPage
      path="/onboarding/domain"
      intro="Add the domain you send from. Minket gives you its records and checks each one."
      asks={[
        {
          title: "A subdomain",
          text: "Sending from mail. or news. keeps the root domain's reputation apart.",
        },
        {
          title: "Its DNS records",
          text: "Three DKIM CNAMEs, the MAIL FROM MX and TXT, DMARC at p=none and the tracking CNAME, written on Cloudflare or copied by hand.",
        },
      ]}
    >
      <ProjectScope
        will="The domain is added to the project in view."
        icon={<DomainsIcon />}
        headingLevel={3}
      >
        {(project) =>
          added ? (
            <Suspense fallback={null}>
              <AddedDomain projectId={project.id} first={added} />
            </Suspense>
          ) : canAdd ? (
            <Suspense fallback={null}>
              <AddDomainForm projectId={project.id} onAdded={setAdded} />
            </Suspense>
          ) : (
            <UnavailableAction
              label="Add domain"
              reason="Viewers cannot add domains"
              align="start"
            />
          )
        }
      </ProjectScope>
    </StepPage>
  );
}
